import { motion } from 'framer-motion';
import { Mic, ClipboardCheck, Copy } from 'lucide-react';

const steps = [
  {
    number: '01',
    title: 'Record',
    description:
      'Open Aura-Chart in your browser or on your phone. Hit record. Speak naturally with your patient — no scripts, no triggers, just your normal conversation.',
    icon: <Mic className="h-7 w-7" />,
  },
  {
    number: '02',
    title: 'Review',
    description:
      'Your SOAP note starts streaming word-by-word within seconds. Review inline, edit anything, or regenerate a section in one click.',
    icon: <ClipboardCheck className="h-7 w-7" />,
  },
  {
    number: '03',
    title: 'Paste',
    description:
      'Copy your note to any EMR — Accuro, OSCAR, TELUS CHR, Jane App. No integration, no IT tickets, no migration. Works with your existing setup on day one.',
    icon: <Copy className="h-7 w-7" />,
  },
];

export default function HowItWorks() {
  return (
    <section className="py-20 md:py-28 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <motion.div
          className="text-center max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <p className="section-label">How Aura-Chart works</p>
          <h2 className="mt-3 section-heading">From conversation to chart in minutes</h2>
          <p className="section-sub">
            Install the Chrome extension. No setup, no training, no EMR integration required.
          </p>
        </motion.div>

        {/* Steps */}
        <div className="mt-16 relative">
          {/* Connector line — desktop only */}
          <div className="hidden md:block absolute top-12 left-0 right-0 h-px bg-gray-200 mx-[calc(100%/6)]" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8 relative">
            {steps.map((step, i) => (
              <motion.div
                key={step.number}
                className="flex flex-col items-center md:items-start text-center md:text-left"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
              >
                {/* Icon circle */}
                <div className="relative z-10 flex items-center justify-center h-24 w-24 rounded-2xl bg-white border border-gray-200 shadow-sm text-primary-600 mb-6">
                  {step.icon}
                  <span className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-primary-600 text-white text-xs font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                </div>

                <h3 className="text-xl font-bold text-gray-900">{step.title}</h3>
                <p className="mt-3 text-base text-gray-500 leading-relaxed max-w-sm">
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Sample SOAP output */}
        <motion.div
          className="mt-16 rounded-2xl overflow-hidden shadow-xl border border-gray-200"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {/* Browser chrome bar */}
          <div className="flex items-center gap-1.5 px-4 py-3 bg-navy-800 border-b border-white/10">
            <span className="h-2.5 w-2.5 rounded-full bg-red-400/60" />
            <span className="h-2.5 w-2.5 rounded-full bg-yellow-400/60" />
            <span className="h-2.5 w-2.5 rounded-full bg-green-400/60" />
            <span className="ml-3 text-xs text-white/30">Aura-Chart — SOAP output</span>
            <span className="ml-auto flex items-center gap-1.5 text-xs text-green-400/80">
              <span className="h-1.5 w-1.5 rounded-full bg-green-400 animate-pulse" />
              Generated in 28s
            </span>
          </div>

          {/* SOAP note body */}
          <div className="bg-white px-6 py-7 md:px-10 md:py-8">
            {/* Header row */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-5 mb-6 border-b border-gray-100">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Family Medicine · Anonymized sample</p>
                <p className="text-xs text-gray-400 mt-0.5">Aura-Chart output — represents real generated format</p>
              </div>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-primary-50 text-primary-700 px-3 py-1.5 rounded-lg whitespace-nowrap">
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" />
                </svg>
                OHIP A007 — General Assessment
              </span>
            </div>

            <div className="grid md:grid-cols-2 gap-6 text-sm">
              {/* S */}
              <div>
                <p className="text-xs font-bold text-primary-600 uppercase tracking-widest mb-2">S — Subjective</p>
                <p className="text-gray-700 leading-relaxed">
                  45M presents with 3-day productive cough and low-grade fever. Onset gradual,
                  worsening past 24h. Yellow sputum. Peak fever 38.2°C at home. Denies chest
                  pain, dyspnea, or haemoptysis. No sick contacts. Vaccinations current.
                </p>
              </div>

              {/* O */}
              <div>
                <p className="text-xs font-bold text-primary-600 uppercase tracking-widest mb-2">O — Objective</p>
                <p className="text-gray-700 leading-relaxed">
                  BP 128/82 &middot; HR 88 &middot; Temp 37.9°C &middot; O₂ 97% RA<br />
                  <span className="text-gray-500">Resp:</span> ↓ air entry RLL, dullness to percussion. No wheeze.<br />
                  <span className="text-gray-500">CVS:</span> Regular rate and rhythm. No murmurs.
                </p>
              </div>

              {/* A */}
              <div>
                <p className="text-xs font-bold text-primary-600 uppercase tracking-widest mb-2">A — Assessment</p>
                <p className="text-gray-700">1. Community-acquired pneumonia <span className="text-gray-400">(J18.9)</span></p>
              </div>

              {/* P */}
              <div>
                <p className="text-xs font-bold text-primary-600 uppercase tracking-widest mb-2">P — Plan</p>
                <p className="text-gray-700 leading-relaxed">
                  1. Amoxicillin 500mg PO TID &times; 7 days<br />
                  2. CXR ordered — RLL infiltrate query<br />
                  3. Return if symptoms worsen or fever persists &gt; 48h<br />
                  4. F/U 1 week or sooner PRN
                </p>
              </div>
            </div>

            <p className="mt-6 pt-4 border-t border-gray-100 text-xs text-gray-400">
              Anonymized sample for demonstration. Notes are generated from your actual
              encounters and never stored on PulseStack servers.
            </p>
          </div>
        </motion.div>

        {/* PLACEHOLDER: Replace the sample above with a real SOAP note screenshot
            (with patient data anonymized) once available from a pilot physician. */}
      </div>
    </section>
  );
}
